import { ConsultCTA } from "../components/ConsultCTA";
import { Icon } from "../components/Icon";
import { PageHero } from "../components/PageHero";
import { Eyebrow, Reveal, SectionHeading } from "../components/Reveal";
import { IMAGES } from "../data/site";
import { usePageMeta } from "../hooks/usePageMeta";

const FOCUS_AREAS = [
    {
        icon: "MessageCircle",
        title: "Functional Communication",
        description:
            "Supporting children in communicating their needs, wants, choices, and feelings in meaningful, everyday ways.",
    },
    {
        icon: "Eye",
        title: "Social Communication",
        description:
            "Gently developing skills such as eye contact, joint attention, turn-taking, and shared enjoyment of interaction.",
    },
    {
        icon: "BookOpen",
        title: "Vocabulary Development",
        description:
            "Building a useful, meaningful vocabulary through play, routines, and activities matched to the child's interests.",
    },
    {
        icon: "PenLine",
        title: "Sentence Formation",
        description:
            "Helping children move from single words toward phrases and sentences they can use in daily life.",
    },
    {
        icon: "HandHeart",
        title: "Requesting",
        description:
            "Encouraging children to ask for what they need — a foundational skill for independence and confidence.",
    },
    {
        icon: "Users",
        title: "Interaction Skills",
        description:
            "Supporting participation in back-and-forth interaction, play, and connection with family and peers.",
    },
    {
        icon: "ListChecks",
        title: "Following Instructions",
        description:
            "Developing understanding and listening skills so children can follow everyday directions and routines.",
    },
    {
        icon: "Heart",
        title: "Parent Guidance",
        description:
            "Equipping parents with practical strategies to support communication at home, where progress truly grows.",
    },
];

const SESSION_STYLE = [
    {
        icon: "Sparkles",
        title: "Child-Friendly & Playful",
        description:
            "Sessions are built around comfortable, engaging, and age-appropriate activities that children genuinely enjoy.",
    },
    {
        icon: "Target",
        title: "Structured & Practical",
        description:
            "Every session works toward clear, meaningful goals chosen around your child's individual needs and strengths.",
    },
    {
        icon: "Users",
        title: "Parents Are Partners",
        description:
            "You are guided throughout, so communication practice continues naturally at home between sessions.",
    },
];

const AutismTherapy = () => {
    usePageMeta(
        "Autism Communication Therapy in Patna | Speech Horizon — Anishabad",
        "Supportive, respectful autism communication therapy in Anishabad, Patna. Functional communication, social interaction, vocabulary, and parent guidance at Speech Horizon."
    );

    return (
        <div data-testid="autism-therapy-page">
            <PageHero
                eyebrow="Autism Therapy"
                lines={["Autism &"]}
                highlight="Communication"
                description="Every autistic child has their own way of experiencing and interacting with the world. Our therapy focuses on supporting communication — never on changing who a child is."
            />

            <section className="bg-cream py-20 md:py-28" data-testid="autism-intro-section">
                <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 md:px-8 lg:grid-cols-2">
                    <div>
                        <SectionHeading
                            align="left"
                            eyebrow="Our Perspective"
                            title="Supportive, Respectful, Individual"
                        />
                        <Reveal delay={0.15}>
                            <div className="mt-6 space-y-5 text-base leading-relaxed text-ink/70 md:text-lg">
                                <p>
                                    For autistic children, therapy may focus on functional communication, social
                                    communication, vocabulary development, sentence formation, requesting, and
                                    interaction skills.
                                </p>
                                <p>
                                    Therapy is always planned around the individual child — their strengths,
                                    interests, sensory preferences, and communication style.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.22}>
                            <div className="mt-8 rounded-2xl border-l-4 border-teal bg-mist p-6">
                                <p className="font-jakarta text-base font-bold leading-snug text-navy md:text-lg">
                                    We do not view autism as something to be &ldquo;cured&rdquo; or
                                    &ldquo;fixed.&rdquo; Our role is to support communication, understanding, and
                                    confidence.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                    <Reveal className="relative">
                        <div className="absolute -left-5 -top-5 h-full w-full rotate-[-2deg] rounded-[2.5rem] bg-teal-soft" />
                        <img
                            src={IMAGES.play}
                            alt="Children playing joyfully outdoors, building social connection"
                            className="relative h-[420px] w-full rounded-[2.5rem] object-cover shadow-xl shadow-navy/10 md:h-[520px]"
                        />
                    </Reveal>
                </div>
            </section>

            <section className="bg-mist py-20 md:py-28" data-testid="autism-focus-section">
                <div className="mx-auto max-w-7xl px-6 md:px-8">
                    <SectionHeading
                        eyebrow="Therapy Focus"
                        title="What Autism Communication Therapy May Focus On"
                        description="Goals are always individualized — these are the areas therapy commonly supports."
                    />
                    <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {FOCUS_AREAS.map((area, i) => (
                            <Reveal key={area.title} delay={i * 0.05}>
                                <div className="group h-full rounded-[1.75rem] border border-navy/5 bg-white p-7 transition-all duration-500 hover:-translate-y-2 hover:border-teal/25 hover:shadow-xl hover:shadow-teal/10">
                                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-teal-soft text-teal-dark transition-transform duration-500 group-hover:scale-110">
                                        <Icon name={area.icon} size={21} strokeWidth={1.9} />
                                    </span>
                                    <h3 className="mt-5 font-jakarta text-base font-bold text-navy">{area.title}</h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-ink/65">{area.description}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="relative overflow-hidden bg-navy py-20 md:py-28" data-testid="autism-sessions-section">
                <div
                    className="pointer-events-none absolute -left-40 bottom-0 h-96 w-96 rounded-full opacity-20 blur-3xl"
                    style={{ background: "radial-gradient(circle, #F4A896 0%, transparent 70%)" }}
                />
                <div className="relative mx-auto max-w-7xl px-6 md:px-8">
                    <SectionHeading
                        dark
                        eyebrow="In Practice"
                        title="What Sessions Look Like"
                    />
                    <div className="mt-14 grid gap-6 md:grid-cols-3">
                        {SESSION_STYLE.map((item, i) => (
                            <Reveal key={item.title} delay={i * 0.08}>
                                <div className="h-full rounded-[1.75rem] border border-white/10 bg-white/5 p-8 backdrop-blur transition-all duration-500 hover:-translate-y-2 hover:border-aqua/30">
                                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-aqua">
                                        <Icon name={item.icon} size={21} strokeWidth={1.9} />
                                    </span>
                                    <h3 className="mt-5 font-jakarta text-lg font-bold text-cream">{item.title}</h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-cream/65">{item.description}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2}>
                        <p className="mx-auto mt-12 max-w-3xl text-center text-base leading-relaxed text-cream/70">
                            Progress looks different for every child — and every step forward in communication is
                            worth celebrating.
                        </p>
                    </Reveal>
                </div>
            </section>

            <ConsultCTA
                title="Book a Consultation"
                text="If you would like to understand your child's communication needs better, a professional consultation is a gentle first step. We are here to listen, guide, and support your family."
            />
        </div>
    );
};

export default AutismTherapy;
